import { colors } from '@/constants/colors'
import { Header } from '@/ui/core/header'
import { Text } from '@/ui/core/typography'
import { View } from 'react-native'
import Animated, { type SharedValue, useAnimatedStyle } from 'react-native-reanimated'

/** Waiting: the figure all but gone, the measure just legible as structure. */
const GHOST = {
	figure: 'rgba(125,111,111,0.13)',
	/** The track carries the numeral's own grey; the fill is a step up from it. */
	track: 'rgba(125,111,111,0.13)',
	bar: 'rgba(125,111,111,0.28)',
}

/** On ink the figure takes the brand's pink and the track is drawn in the ground's own white. */
const ON_INK = {
	track: 'rgba(255,255,255,0.20)',
	bar: colors.accent,
}

/**
 * Flat on the pink, with no card under it. The track cannot be the ground it sits on, so it takes
 * the blush a step lighter — the same pair the ground and its cards use everywhere else.
 */
const ON_GROUND = { track: colors.blush, bar: colors.accent }

export type ScoreTone = 'white' | 'ink' | 'ground'

/**
 * The score against its own range, filling on the same run as the count so the bar and the
 * numeral arrive together. The far end is labelled rather than the number denominated: the scale
 * is shown, not stated.
 */
const Band = ({
	progress,
	tone,
	ghost,
}: { progress: SharedValue<number>; tone: ScoreTone; ghost?: boolean }) => {
	const fill = useAnimatedStyle(() => ({
		width: `${Math.max(0, Math.min(100, progress.value))}%`,
	}))
	const track = ghost
		? GHOST.track
		: tone === 'ink'
		  ? ON_INK.track
		  : tone === 'ground'
			  ? ON_GROUND.track
			  : colors.ground
	const bar = ghost ? GHOST.bar : colors.accent

	return (
		<View style={{ marginTop: -34, paddingHorizontal: 8 }}>
			<Text
				size="body3"
				tone={tone === 'ink' ? 'white' : 'muted'}
				className={tone === 'ink' ? 'text-right opacity-60' : 'text-right'}
			>
				100
			</Text>
			<View
				className="mt-1 h-1.5 overflow-hidden rounded-full"
				style={{ backgroundColor: track }}
			>
				<Animated.View
					className="absolute top-0 bottom-0 left-0 rounded-full"
					style={[fill, { backgroundColor: bar }]}
				/>
			</View>
			<View className="h-2" />
		</View>
	)
}

/**
 * The figure and its measure: one reading, wherever the score is shown. Care's card wears it on
 * white, the score page flat on the pink with no card at all. Driven from outside, so each owner
 * runs its own motion.
 */
export const ScoreReading = ({
	shown,
	progress,
	tone = 'white',
	ghost,
}: {
	shown: number
	progress: SharedValue<number>
	tone?: ScoreTone
	ghost?: boolean
}) => (
	<>
		<Header
			variant="display-lg"
			tone={tone === 'ink' ? 'ground' : 'ink'}
			className="mt-8"
			style={ghost ? { color: GHOST.figure } : undefined}
		>
			{shown}
		</Header>
		<Band progress={progress} tone={tone} ghost={ghost} />
	</>
)
